import {
	getFavoriteWorkshopsOptions,
	getProfileOptions,
} from "@/api/queryOptions";
import { useSuspenseQuery } from "@tanstack/react-query";
import { objectToArray } from "@/utils/helpers";
import { Link } from "@tanstack/react-router";
import CoverCardNewVisual from "@/components/shared/CoverCardNewVisual";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
	AccordionTriggerEnd,
} from "@/components/ui/accordion";
import { EndOfTheListMessage } from "@/components/ui/EndOfTheListMessage";

interface MyAcademyWorkshopsAccordionProps {
	mainCategory: "TRAININGS" | "WEBINARS";
	onSubCategoryChange: (
		subCategory: "UPCOMING" | "RECORDED" | "FAVORITES" | "NONE"
	) => void;
}

function isTrainingPostArray(array: unknown): array is Array<{
	type: number;
	wp_data: unknown;
	id: string | number;
	name: string;
	slug: string;
}> {
	return (
		Array.isArray(array) &&
		array.every(
			(post) =>
				post &&
				typeof post === "object" &&
				"type" in post &&
				typeof post.type === "number"
		)
	);
}

function isOwnedCourseArray(array: unknown): array is Array<{
	id: string | number;
	product: {
		type: number;
		wp_data: unknown;
		id: string | number;
		name: string;
		slug: string;
	};
}> {
	return (
		Array.isArray(array) &&
		array.every(
			(item) =>
				item &&
				typeof item === "object" &&
				"product" in item &&
				item.product &&
				typeof item.product === "object" &&
				"type" in item.product &&
				typeof item.product.type === "number"
		)
	);
}

function isFavoriteArray(array: unknown): array is Array<{
	type: number;
	wp_data: unknown;
	id: string | number;
	name: string;
	slug: string;
}> {
	return (
		Array.isArray(array) &&
		array.every(
			(post) =>
				post &&
				typeof post === "object" &&
				"type" in post &&
				typeof post.type === "number"
		)
	);
}

const MyAcademyWorkshopsAccordion = ({
	mainCategory,
	onSubCategoryChange,
}: MyAcademyWorkshopsAccordionProps): JSX.Element => {
	const { data: profile } = useSuspenseQuery(getProfileOptions);

	const { data: favoriteWorkshops } = useSuspenseQuery(
		getFavoriteWorkshopsOptions
	);

	const futureduserRaw = profile?.courses?.futureduser;
	const futureduserRawArray = objectToArray(futureduserRaw ?? {});
	const upcomingTrainings = isTrainingPostArray(futureduserRawArray)
		? futureduserRawArray.filter((post) => post.type === 6)
		: [];

	const ownedCoursesRaw = profile?.courses?.ownedCourses;
	const ownedCoursesArray =
		ownedCoursesRaw &&
		typeof ownedCoursesRaw === "object" &&
		!Array.isArray(ownedCoursesRaw)
			? objectToArray(ownedCoursesRaw)
			: ownedCoursesRaw;
	const ownedTrainings = isOwnedCourseArray(ownedCoursesArray)
		? ownedCoursesArray.filter((item) => item.product.type === 3)
		: [];

	const favoritesRaw = favoriteWorkshops?.favorites;
	const favoriteTrainings = isFavoriteArray(favoritesRaw)
		? favoritesRaw.filter((post) => post.type === 3 || post.type === 6)
		: [];

	const upcomingWebinars = isTrainingPostArray(futureduserRawArray)
		? futureduserRawArray.filter((post) => post.type === 7)
		: [];

	const ownedWebinars = isOwnedCourseArray(ownedCoursesArray)
		? ownedCoursesArray.filter((item) => item.product.type === 4)
		: [];

	const favoriteWebinars = isFavoriteArray(favoritesRaw)
		? favoritesRaw.filter((post) => post.type === 4 || post.type === 7)
		: [];

	// Map accordion item value to subCategory
	const handleAccordionValueChange = (value: string): void => {
		if (!value || value === "") {
			onSubCategoryChange("NONE");
			return;
		}

		// Map item values to subCategory
		switch (value) {
			case "item-1":
				onSubCategoryChange("UPCOMING");
				break;
			case "item-2":
				onSubCategoryChange("RECORDED");
				break;
			case "item-3":
				onSubCategoryChange("FAVORITES");
				break;
			default:
				onSubCategoryChange("NONE");
		}
	};

	return (
		<>
			{/* START TRAININGS */}
			{mainCategory === "TRAININGS" && profile && (
				<>
					<Accordion
						type="single"
						collapsible
						className="w-full mb-10"
						onValueChange={handleAccordionValueChange}
					>
						<AccordionItem value="item-1">
							<AccordionTrigger>Nadchodzące</AccordionTrigger>
							<AccordionContent>
								<div className="flex flex-col gap-3">
									{isTrainingPostArray(futureduserRawArray) ? (
										upcomingTrainings.length === 0 ? (
											<EndOfTheListMessage>
												Już niedługo pojawią się tu nasze kolejne wydarzenia!
											</EndOfTheListMessage>
										) : (
											upcomingTrainings.map((post) => (
												<Link
													key={`CoverCardMVP-workshops--${post.id}`}
													params={{ slugId: `${post.slug}` }}
													search={{
														status: "Kurs-online",
													}}
													to={`/workshops/training/$slugId/auth`}
												>
													<CoverCardNewVisual
														authors={post.wp_data.authors_users_data}
														categories={post.wp_data.kategoria}
														coverId={post.id}
														title={post.name}
														coverDate={Number(post.wp_data.starts_at)}
														slug={post.slug}
													/>
												</Link>
											))
										)
									) : (
										<EndOfTheListMessage>
											Próba wczytania listy nie powiodła się. Spróbuj później
										</EndOfTheListMessage>
									)}
								</div>
								{upcomingTrainings.length > 1 && (
									<AccordionTriggerEnd>
										Zwiń sekcję nadchodzące szkolenia
									</AccordionTriggerEnd>
								)}
							</AccordionContent>
						</AccordionItem>
						<AccordionItem value="item-2">
							<AccordionTrigger>Rozpoczęte</AccordionTrigger>
							<AccordionContent>
								<div className="flex flex-col gap-3">
									{isOwnedCourseArray(ownedCoursesArray) ? (
										ownedTrainings.length === 0 ? (
											<EndOfTheListMessage>
												Już niedługo pojawią się tu nasze kolejne wydarzenia!
											</EndOfTheListMessage>
										) : (
											ownedTrainings.map((post) => (
												<Link
													key={`CoverCardMVP-workshops--${post.id}`}
													params={{ slugId: `${post.product.slug}` }}
													search={{
														status: "Szkolenie",
													}}
													to={`/workshops/training/$slugId/auth`}
												>
													<CoverCardNewVisual
														authors={post.product.wp_data?.authors_users_data}
														categories={post.product.wp_data?.kategoria}
														coverId={post.product.id}
														title={post.product.name}
														coverDate={Number(
															post.product.wp_data?.["legal-status"]
														)}
														slug={post.product.slug}
													/>
												</Link>
											))
										)
									) : (
										<EndOfTheListMessage>
											Próba wczytania listy nie powiodła się. Spróbuj później
										</EndOfTheListMessage>
									)}
								</div>
								{ownedTrainings.length > 1 && (
									<AccordionTriggerEnd>
										Zwiń sekcję rozpoczęte szkolenia
									</AccordionTriggerEnd>
								)}
							</AccordionContent>
						</AccordionItem>
						<AccordionItem value="item-3">
							<AccordionTrigger>&#9829;&nbsp; Ulubione</AccordionTrigger>
							<AccordionContent>
								<div className="flex flex-col gap-3">
									{isFavoriteArray(favoritesRaw) ? (
										favoriteTrainings.length === 0 ? (
											<EndOfTheListMessage>
												Nie masz jeszcze żadnych szkoleń dodanych do ulubionych
											</EndOfTheListMessage>
										) : (
											favoriteTrainings.map((post) => (
												<Link
													key={`CoverCardMVP-workshops--${post.id}`}
													params={{ slugId: `${post.slug}` }}
													search={{
														status:
															post.type === 3 ? "Szkolenie" : "Kurs-online",
													}}
													to={`/workshops/training/$slugId/auth`}
												>
													<CoverCardNewVisual
														authors={post.wp_data.authors_users_data}
														categories={post.wp_data.kategoria}
														coverId={post.id}
														title={post.name}
														coverDate={
															Number(post.wp_data["legal-status"])
																? Number(post.wp_data["legal-status"])
																: Number(post.wp_data.starts_at)
														}
														slug={post.slug}
													/>
												</Link>
											))
										)
									) : (
										<EndOfTheListMessage>
											Próba wczytania listy nie powiodła się. Spróbuj później
										</EndOfTheListMessage>
									)}
								</div>
								{favoriteTrainings.length > 1 && (
									<AccordionTriggerEnd>
										Zwiń sekcję ulubione szkolenia
									</AccordionTriggerEnd>
								)}
							</AccordionContent>
						</AccordionItem>
					</Accordion>
					{/* END TRAININGS */}
				</>
			)}
			{mainCategory === "WEBINARS" && profile && (
				<>
					{/* START WEBINARS */}
					<Accordion
						type="single"
						collapsible
						className="w-full mb-10"
						onValueChange={handleAccordionValueChange}
					>
						<AccordionItem value="item-1">
							<AccordionTrigger>Nadchodzące</AccordionTrigger>
							<AccordionContent>
								<div className="flex flex-col gap-3">
									{isTrainingPostArray(futureduserRawArray) ? (
										upcomingWebinars.length === 0 ? (
											<EndOfTheListMessage>
												Nie jestes zapisana/y na żaden z nadchodzących webinarów
											</EndOfTheListMessage>
										) : (
											upcomingWebinars.map((post) => (
												<Link
													key={`CoverCardMVP-workshops--${post.id}`}
													params={{ slugId: `${post.slug}` }}
													search={{
														status: "Webinar-online",
													}}
													to={`/workshops/webinar/$slugId/auth`}
												>
													<CoverCardNewVisual
														authors={post.wp_data.authors_users_data}
														categories={post.wp_data.kategoria}
														coverId={post.id}
														title={post.name}
														coverDate={Number(post.wp_data.starts_at)}
														slug={post.slug}
													/>
												</Link>
											))
										)
									) : (
										<EndOfTheListMessage>
											Próba wczytania listy nie powiodła się. Spróbuj później
										</EndOfTheListMessage>
									)}
								</div>
								{upcomingWebinars.length > 1 && (
									<AccordionTriggerEnd>
										Zwiń sekcję nadchodzące webinary
									</AccordionTriggerEnd>
								)}
							</AccordionContent>
						</AccordionItem>
						<AccordionItem value="item-2">
							<AccordionTrigger>Rozpoczęte</AccordionTrigger>
							<AccordionContent>
								<div className="flex flex-col gap-3">
									{isOwnedCourseArray(ownedCoursesArray) ? (
										ownedWebinars.length === 0 ? (
											<EndOfTheListMessage>
												Już niedługo pojawią się tu nasze kolejne wydarzenia!
											</EndOfTheListMessage>
										) : (
											ownedWebinars.map((post) => (
												<Link
													key={`CoverCardMVP-workshops--${post.id}`}
													params={{ slugId: `${post.product.slug}` }}
													search={{
														status: "Webinar",
													}}
													to={`/workshops/webinar/$slugId/auth`}
												>
													<CoverCardNewVisual
														authors={post.product.wp_data.authors_users_data}
														categories={post.product.wp_data.kategoria}
														coverId={post.product.id}
														title={post.product.name}
														coverDate={Number(
															post.product.wp_data["legal-status"]
														)}
														slug={post.product.slug}
													/>
												</Link>
											))
										)
									) : (
										<EndOfTheListMessage>
											Próba wczytania listy nie powiodła się. Spróbuj później
										</EndOfTheListMessage>
									)}
								</div>
								{ownedWebinars.length > 1 && (
									<AccordionTriggerEnd>
										Zwiń sekcję rozpoczęte webinary
									</AccordionTriggerEnd>
								)}
							</AccordionContent>
						</AccordionItem>
						<AccordionItem value="item-3">
							<AccordionTrigger>&#9829;&nbsp; Ulubione</AccordionTrigger>
							<AccordionContent>
								<div className="flex flex-col gap-3">
									{isFavoriteArray(favoritesRaw) ? (
										favoriteWebinars.length === 0 ? (
											<EndOfTheListMessage>
												Nie masz jeszcze żadnych webinarów dodanych do
												ulubionych
											</EndOfTheListMessage>
										) : (
											favoriteWebinars.map((post) => (
												<Link
													key={`CoverCardMVP-workshops--${post.id}`}
													params={{ slugId: `${post.slug}` }}
													search={{
														status:
															post.type === 4 ? "Webinar" : "Webinar-online",
													}}
													to={`/workshops/webinar/$slugId/auth`}
												>
													<CoverCardNewVisual
														authors={post.wp_data.authors_users_data}
														categories={post.wp_data.kategoria}
														coverId={post.id}
														title={post.name}
														coverDate={
															Number(post.wp_data["legal-status"])
																? Number(post.wp_data["legal-status"])
																: Number(post.wp_data.starts_at)
														}
														slug={post.slug}
													/>
												</Link>
											))
										)
									) : (
										<EndOfTheListMessage>
											Próba wczytania listy nie powiodła się. Spróbuj później
										</EndOfTheListMessage>
									)}
								</div>
								{favoriteWebinars.length > 1 && (
									<AccordionTriggerEnd>
										Zwiń sekcję ulubione webinary
									</AccordionTriggerEnd>
								)}
							</AccordionContent>
						</AccordionItem>
					</Accordion>
					{/* END WEBINARS */}
				</>
			)}
		</>
	);
};

export default MyAcademyWorkshopsAccordion;
